import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Stack, Typography, Paper, TextField, Button, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Chip, Box, InputAdornment,
  CircularProgress, IconButton, Tooltip,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/SyncOutlined";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import LaunchIcon from "@mui/icons-material/OpenInNewOutlined";
import LocalShippingIcon from "@mui/icons-material/LocalShippingOutlined";
import { apiGet, apiPost, ApiError } from "../api/client";
import ImageGalleryCell from "../components/ImageGalleryCell";
import { useToast } from "../ui/Toaster";

function useDebounced(v, ms = 300) {
  const [d, setD] = useState(v);
  useEffect(() => { const t = setTimeout(() => setD(v), ms); return () => clearTimeout(t); }, [v, ms]);
  return d;
}

// Status chip color mapping mirrors carriers/base.py STATUSES.
const STATUS_META = {
  label_created:      { label: "label created",      color: "default" },
  in_transit:         { label: "in transit",         color: "info" },
  out_for_delivery:   { label: "out for delivery",   color: "warning" },
  delivered:          { label: "delivered",          color: "success" },
  delivery_exception: { label: "exception",          color: "error" },
  returned:           { label: "returned",           color: "secondary" },
  unknown:            { label: "unknown",            color: "default" },
};

function TrackingStatusChip({ status }) {
  const meta = STATUS_META[status] || STATUS_META.unknown;
  return (
    <Chip
      size="small"
      label={meta.label}
      color={meta.color}
      variant={meta.color === "default" ? "outlined" : "filled"}
      sx={{ height: 22, fontSize: 11, fontWeight: 600, textTransform: "capitalize" }}
    />
  );
}

const STATUS_FILTERS = [
  { value: "",                   label: "All" },
  { value: "in_transit",         label: "In transit" },
  { value: "out_for_delivery",   label: "Out for delivery" },
  { value: "delivered",          label: "Delivered" },
  { value: "delivery_exception", label: "Exception" },
  { value: "label_created",      label: "Pending scan" },
  { value: "unknown",            label: "Unknown" },
];

function trackingUrlFor(carrier, tn) {
  if (!tn) return null;
  const t = encodeURIComponent(tn);
  switch ((carrier || "").toLowerCase()) {
    case "usps":  return `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${t}`;
    case "ups":   return `https://www.ups.com/track?loc=en_US&tracknum=${t}`;
    case "fedex": return `https://www.fedex.com/fedextrack/?trknbr=${t}`;
    default:      return null;
  }
}

function fmtRelAge(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return "";
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

function fmtDateShort(epoch) {
  if (!epoch) return "";
  const d = new Date(Number(epoch) * 1000);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function Shipments() {
  const { error: toastError, success: toastSuccess } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debounced = useDebounced(search, 300);
  const [statusFilter, setStatusFilter] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  // Background prefetch progress polled from /api/tracking/prefetch_status.
  // Same shared worker the Funds page uses, so a refresh kicked off from
  // either page populates the cache for both.
  const [prefetch, setPrefetch] = useState(null);

  const load = useCallback(async (signal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      const url = `/api/shipments${params.toString() ? `?${params.toString()}` : ""}`;
      const res = await apiGet(url, { signal });
      setRows(Array.isArray(res?.results) ? res.results : []);
    } catch (e) {
      if (e?.name === "AbortError") return;
      toastError("Couldn't load shipments", e instanceof ApiError ? e.message : String(e));
      setRows([]);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [statusFilter, toastError]);

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  // Poll the prefetch worker while it's running and re-load the page
  // every 30s so newly-cached carrier data shows up progressively (USPS
  // scrapes through nodriver are slow — ~3-5s each — so a 200-package
  // batch can take 10+ minutes; the user gets to watch it fill in).
  useEffect(() => {
    let cancelled = false;
    let lastReload = 0;
    const tick = async () => {
      if (cancelled) return;
      try {
        const s = await apiGet("/api/tracking/prefetch_status");
        if (cancelled) return;
        setPrefetch(s);
        if (s?.running && Date.now() - lastReload > 30000) {
          lastReload = Date.now();
          load();
        } else if (!s?.running && prefetch?.running) {
          // Just finished — final reload so the last batch lands.
          load();
        }
      } catch { /* best-effort */ }
    };
    const id = setInterval(tick, 4000);
    tick();
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefetch?.running, load]);

  // Frontend-side text filter (search) to keep load() cheap. Backend already
  // filters by status/platform server-side.
  const filtered = useMemo(() => {
    const q = debounced.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.title || "").toLowerCase().includes(q) ||
      (r.cleaned_title || "").toLowerCase().includes(q) ||
      (r.tracking_number || "").toLowerCase().includes(q) ||
      (r.order_id || "").toLowerCase().includes(q) ||
      (r.lot_ids || []).some((l) => (l || "").toLowerCase().includes(q))
    );
  }, [rows, debounced]);

  // Counts per status for the filter chips header.
  const counts = useMemo(() => {
    const c = {};
    for (const r of rows) {
      const s = r.tracking?.status || "unknown";
      c[s] = (c[s] || 0) + 1;
    }
    return c;
  }, [rows]);

  const refreshSingle = async (row) => {
    if (!row.tracking_number) return;
    try {
      const res = await apiGet(
        `/api/tracking/${encodeURIComponent(row.tracking_number)}?refresh=1`
      );
      // Splice the fresh tracking back into the row in-place.
      setRows((cur) => cur.map((r) =>
        r.tracking_number === row.tracking_number
          ? { ...r, tracking: res, tracking_age: 0, tracking_error: res?.error || null, carrier: res?.carrier || r.carrier }
          : r
      ));
      if (res?.error) {
        toastError("Tracking refresh failed", res.error);
      } else {
        toastSuccess("Refreshed", `${res?.status || "ok"}`);
      }
    } catch (e) {
      toastError("Refresh failed", e instanceof ApiError ? e.message : String(e));
    }
  };

  const refreshAll = async () => {
    // Hand the work to /api/tracking/prefetch — runs in a background
    // thread on the server so the request returns immediately. The
    // poller above watches /prefetch_status and re-pulls /api/shipments
    // every 30s so the table fills in as carriers respond. USPS is
    // scraper-driven (3-5s per package via nodriver) so blocking the
    // request thread on a big batch would timeout the browser.
    const tns = filtered.map((r) => r.tracking_number).filter(Boolean);
    setRefreshing(true);
    try {
      // Empty body → server picks every non-delivered tracking. When the
      // user has filtered the table down (e.g. status=in_transit only),
      // pass that subset so we don't waste scrapes on already-delivered
      // packages they're not looking at.
      const body = tns.length && tns.length < rows.length
        ? { tracking_numbers: tns }
        : {};
      const res = await apiPost("/api/tracking/prefetch", body);
      if (res?.started) {
        toastSuccess("Tracking refresh started", `${res.total} package${res.total === 1 ? "" : "s"} queued`);
      } else if (res?.reason === "already running") {
        toastSuccess("Already refreshing", `${res.done}/${res.total} done`);
      }
      load();
    } catch (e) {
      toastError("Couldn't start refresh", e instanceof ApiError ? e.message : String(e));
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "flex-start" }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: { xs: 2, md: 3.5 } }}
      >
        <Box>
          <Typography variant="h1" sx={{ mb: 0.5, fontSize: { xs: "1.85rem", md: "2.5rem" } }}>
            Shipments
          </Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
            {filtered.length.toLocaleString()} of {rows.length.toLocaleString()} package{rows.length === 1 ? "" : "s"}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {prefetch?.running && (
            <Stack direction="row" alignItems="center" gap={0.75} sx={{ mr: 1 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Refreshing: {prefetch.done}/{prefetch.total}
                {prefetch.errors ? ` · ${prefetch.errors} err` : ""}
              </Typography>
            </Stack>
          )}
          <Button
            startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <SyncIcon />}
            variant="outlined"
            onClick={() => load()}
            disabled={loading}
          >
            Reload
          </Button>
          <Button
            startIcon={refreshing ? <CircularProgress size={15} color="inherit" /> : <RefreshIcon />}
            variant="contained"
            onClick={refreshAll}
            disabled={refreshing || prefetch?.running || filtered.length === 0}
          >
            {refreshing ? "Starting…" : prefetch?.running ? "Refreshing…" : "Refresh tracking"}
          </Button>
        </Stack>
      </Stack>

      {/* Filters card */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack spacing={1.5}>
          <TextField
            placeholder="Search title, lot, tracking, order…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
            <Typography variant="caption" sx={{ color: "text.secondary", mr: 0.5 }}>
              Status:
            </Typography>
            {STATUS_FILTERS.map((s) => {
              const active = statusFilter === s.value;
              const count = s.value ? counts[s.value] : rows.length;
              return (
                <Chip
                  key={s.value || "all"}
                  size="small"
                  label={`${s.label}${count !== undefined ? ` (${count || 0})` : ""}`}
                  variant={active ? "filled" : "outlined"}
                  color={active ? "primary" : "default"}
                  onClick={() => setStatusFilter(s.value)}
                />
              );
            })}
          </Stack>
        </Stack>
      </Paper>

      {/* Table */}
      <Paper variant="outlined" sx={{ overflow: "hidden", position: "relative" }}>
        <TableContainer>
          <Table size="small" sx={{ "& td, & th": { whiteSpace: "nowrap" } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 80 }}>Image</TableCell>
                <TableCell>Order</TableCell>
                <TableCell sx={{ width: 270 }}>Tracking</TableCell>
                <TableCell sx={{ width: 110 }}>ETA</TableCell>
                <TableCell sx={{ width: 110 }}>Sold</TableCell>
                <TableCell sx={{ width: 110 }}>Refreshed</TableCell>
                <TableCell sx={{ width: 80 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, color: "text.secondary" }}>
                    <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                      <CircularProgress size={14} />
                      <span>Loading…</span>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Stack alignItems="center" gap={1} sx={{ color: "text.secondary" }}>
                      <LocalShippingIcon fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {rows.length === 0 ? "No shipments yet" : "No matches"}
                      </Typography>
                      {rows.length === 0 && (
                        <Typography variant="caption">
                          Sales with tracking numbers will appear here once eBay reports them.
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((row) => {
                const td = row.tracking || {};
                const status = td.status || "unknown";
                const carrier = (td.carrier || row.carrier || "").toLowerCase();
                const trackUrl = trackingUrlFor(carrier, row.tracking_number);
                const lastEvent = td.events?.[0];
                return (
                  <TableRow key={row.sale_id ?? row.tracking_number} hover>
                    <TableCell sx={{ width: 80 }}>
                      <ImageGalleryCell imageUrlsStr={row.image_urls} thumbnailSize={48} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ minWidth: 0, maxWidth: 360 }}>
                        <Typography variant="body2" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {row.cleaned_title || row.title || "—"}
                        </Typography>
                        <Typography
                          variant="caption" color="text.secondary"
                          sx={{ fontFamily: '"Geist Mono", monospace', display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        >
                          {row.lot_ids?.[0] || row.order_id || "—"}
                          {row.lot_ids && row.lot_ids.length > 1 && (
                            <Box component="span" sx={{ ml: 0.75, color: "text.disabled" }}>
                              +{row.lot_ids.length - 1}
                            </Box>
                          )}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                          <TrackingStatusChip status={status} />
                          {carrier && (
                            <Chip
                              size="small" variant="outlined"
                              label={carrier.toUpperCase()}
                              sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                            />
                          )}
                          {row.tracking_error && (
                            <Tooltip title={row.tracking_error}>
                              <Chip size="small" color="warning" variant="outlined"
                                label="error" sx={{ height: 20, fontSize: 10 }} />
                            </Tooltip>
                          )}
                        </Stack>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {trackUrl ? (
                            <a
                              href={trackUrl} target="_blank" rel="noopener noreferrer"
                              style={{ fontFamily: '"Geist Mono", monospace', fontSize: 11, color: "inherit" }}
                            >
                              {row.tracking_number}
                            </a>
                          ) : (
                            <Typography sx={{ fontFamily: '"Geist Mono", monospace', fontSize: 11, color: "text.secondary" }}>
                              {row.tracking_number}
                            </Typography>
                          )}
                        </Stack>
                        {(td.location || lastEvent?.description) && (
                          <Typography variant="caption" color="text.secondary" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                            {td.location && <Box component="span">{td.location}</Box>}
                            {td.location && lastEvent?.description && " · "}
                            {lastEvent?.description}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {td.estimated_delivery
                        ? new Date(td.estimated_delivery + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })
                        : (td.delivered_at
                            ? new Date(td.delivered_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
                            : "—")}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {fmtDateShort(row.sold_date) || "—"}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary", fontSize: 11 }}>
                      {row.tracking_age != null ? fmtRelAge(row.tracking_age) : "never"}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Refresh tracking from carrier">
                        <span>
                          <IconButton size="small" onClick={() => refreshSingle(row)} disabled={!row.tracking_number}>
                            <RefreshIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {trackUrl && (
                        <Tooltip title="Open carrier page">
                          <IconButton size="small" component="a" href={trackUrl} target="_blank" rel="noopener noreferrer">
                            <LaunchIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {loading && filtered.length > 0 && (
          <Box sx={{
            position: "absolute", top: 8, right: 12,
            display: "inline-flex", alignItems: "center", gap: 0.75,
            bgcolor: "background.paper", border: 1, borderColor: "divider",
            borderRadius: 1, px: 1, py: 0.25, fontSize: "0.7rem",
            color: "text.secondary",
          }}>
            <CircularProgress size={12} /> Refreshing
          </Box>
        )}
      </Paper>
    </Box>
  );
}
